import React, { useState } from 'react';
import { 
  Home, 
  TreePine, 
  Layers, 
  Square, 
  Shield, 
  ChevronRight,
  ExternalLink 
} from 'lucide-react';

const Services: React.FC = () => {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: Shield,
      title: 'Menuiseries Extérieures & Fermetures',
      subtitle: 'Protection et esthétique',
      description: 'Solutions complètes pour sécuriser et embellir vos ouvertures extérieures.',
      details: [
        'Volets battants et coulissants',
        'Persiennes traditionnelles',
        'Stores et protections solaires',
        'Bardages bois sur mesure',
        'Fenêtres de toit et lucarnes',
        'Portails et clôtures',
        'Pergolas et auvents'
      ],
      image: '/images/medley.png'
    },
    {
      icon: TreePine,
      title: 'Agencements Extérieurs',
      subtitle: 'Votre espace de vie en plein air',
      description: 'Créons ensemble vos espaces extérieurs pour profiter pleinement de votre jardin.',
      details: [
        'Terrasses bois et composite',
        'Platelages et pontons',
        'Pergolas bioclimatiques',
        'Abris de piscine',
        'Mobilier de jardin sur mesure',
        'Clôtures décoratives',
        'Structures d\'ombrage'
      ],
      image: 'amenagements-ext'
    },
    {
      icon: Home,
      title: 'Agencement Intérieur & Mobilier',
      subtitle: 'Des espaces optimisés sur mesure',
      description: 'Optimisation de vos espaces intérieurs avec du mobilier conçu spécialement pour vous.',
      details: [
        'Cuisines équipées sur mesure',
        'Dressings et placards',
        'Bibliothèques et étagères',
        'Plans de travail bois/pierre',
        'Escaliers et garde-corps',
        'Aménagements sous pente',
        'Mobilier contemporain'
      ],
      image: 'agencement-int'
    },
    {
      icon: Square,
      title: 'Sols & Parquets',
      subtitle: 'Le confort sous vos pieds',
      description: 'Pose professionnelle de revêtements de sol pour tous types d\'espaces.',
      details: [
        'Parquets massifs et contrecollés',
        'Pose collée et flottante',
        'Stratifiés haute qualité',
        'Revêtements souples (LVT, PVC)',  
        'Finitions et plinthes',
        'Rénovation de parquets anciens',
        'Surfaces jusqu\'à 150m²'
      ],
      image: 'sols-parquets'
    },
    {
      icon: Layers,
      title: 'Isolation, Plâtrerie & Plafonds',
      subtitle: 'Confort thermique et acoustique',
      description: 'Solutions d\'isolation et d\'aménagement pour votre confort quotidien.',
      details: [
        'Isolation intérieure thermique',
        'Isolation phonique',
        'Pose de plaques de plâtre',
        'Faux plafonds décoratifs',
        'Plafonds tendus',
        'Cloisons et doublages',
        'Finitions et enduits'
      ],
      image: 'isolation-platerie'
    }
  ];

  return (
    <section id="prestations" className="py-20 bg-stone-50">
      <div className="container-max section-padding">
        {/* En-tête */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6">
            Nos <span className="text-wood-600">Prestations</span>
          </h2>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto">
            Un savoir-faire complet pour tous vos projets d'agencement intérieur et extérieur. 
            De la conception à la réalisation, nous vous accompagnons à chaque étape.
          </p>
        </div>

        {/* Navigation des services */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-4">
            {services.map((service, index) => (
              <button
                key={index}
                onClick={() => setActiveService(index)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  activeService === index
                    ? 'bg-wood-600 text-white shadow-lg'
                    : 'bg-white text-stone-700 hover:bg-wood-50 hover:text-wood-600 border border-stone-200'
                }`}
              >
                <service.icon className="w-5 h-5" />
                <span className="hidden sm:inline">{service.title}</span>
                <span className="sm:hidden">{service.title.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Détail du service actif */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            {/* Contenu - 3 colonnes */}
            <div className="lg:col-span-3 p-8 lg:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-wood-100 rounded-lg flex items-center justify-center">
                  {React.createElement(services[activeService].icon, { className: "w-6 h-6 text-wood-600" })}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-stone-900 mb-2">
                    {services[activeService].title}
                  </h3>
                  <p className="text-wood-600 font-medium">
                    {services[activeService].subtitle}
                  </p>
                </div>
              </div>

              <p className="text-stone-600 text-lg mb-8 leading-relaxed">
                {services[activeService].description}
              </p>

              <div className="space-y-4 mb-8">
                <h4 className="font-semibold text-stone-900">Nos prestations incluent :</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {services[activeService].details.map((detail, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <ChevronRight className="w-4 h-4 text-wood-600 flex-shrink-0" />
                      <span className="text-stone-700">{detail}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  className="btn-primary flex items-center justify-center space-x-2"
                  onClick={() => {
                    const section = document.getElementById('contact');
                    section?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <span>Demander un devis</span>
                  <ExternalLink className="w-4 h-4" />
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    const section = document.getElementById('realisations');
                    section?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Voir nos réalisations
                </button>
              </div>
            </div>

            {/* Image - 2 colonnes */}
            <div className="lg:col-span-2 bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center relative p-6 lg:p-8">
              {services[activeService].image && services[activeService].image.startsWith('/images/') ? (
                <div className="w-full h-full flex items-center justify-center">
                  <img
                    src={services[activeService].image}
                    alt={services[activeService].title}
                    className="w-full max-w-sm object-cover rounded-2xl shadow-2xl"
                  />
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 bg-wood-600 rounded-lg mx-auto opacity-80 flex items-center justify-center">
                    {React.createElement(services[activeService].icon, { className: "w-10 h-10 text-white" })}
                  </div>
                  <p className="text-lg font-medium text-stone-700">
                    Photos de réalisations
                  </p>
                  <p className="text-sm text-stone-500">
                    {services[activeService].title}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Call to action global */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-wood-600 to-wood-700 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Un projet en tête ?
            </h3>
            <p className="text-lg mb-8 opacity-90">
              Contactez-nous pour un devis personnalisé et gratuit. 
              Nous étudions chaque demande avec attention et vous proposons 
              la solution la mieux adaptée à vos besoins et votre budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+33676825228"
                className="bg-white text-wood-600 px-8 py-3 rounded-lg font-medium hover:bg-stone-50 transition-colors duration-200 inline-block text-center"
              >
                Appeler maintenant
              </a>
              <button 
                className="bg-wood-500 text-white px-8 py-3 rounded-lg font-medium hover:bg-wood-400 transition-colors duration-200"
                onClick={() => {
                  const section = document.getElementById('contact');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Demander un devis
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
